import React, { useContext } from 'react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import CardContent from '@material-ui/core/CardContent';
import TextField from '@material-ui/core/TextField';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { PlateItRightImageThumbnail } from '../../../features/PpsiContainer/constants';
import FormContext from '../../Form/FormContext';
import ImageWithDropzone from '../../Image/ImageWithDropzone';
import useUtensils from '../../../features/UtensilContainer/hooks/useUtensils';
import { getLanguage } from '../../../features/app/selectors';
import { getPpiDataForIds } from '../../../features/PpiContainer/selectors';
import { sortBy } from 'lodash-es';
import TextEditor from 'components/common/TextEditor';

const useStyles = makeStyles(theme => ({
  cardContentFieldsRoot: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`
  },
  menuGlobalSelect: {
    backgroundColor: '#d3e1ff',
    '&:hover': {
      backgroundColor: '#ffffff'
    },
    '&.Mui-selected': {
      backgroundColor: '#b2b2b2'
    }
  },
  menuLocalSelect: {
    backgroundColor: '#d8ffd8',
    '&:hover': {
      backgroundColor: '#ffffff'
    },
    '&.Mui-selected': {
      backgroundColor: '#b2b2b2'
    }
  }
}));

function getPpiDataIdsFromPpiDetails(ppiDetails = []) {
  return ppiDetails
    .filter(ppiDetail => ppiDetail.ppiEnrichmentDataId)
    .map(ppiDetail => ppiDetail.ppiEnrichmentDataId);
}

const StepCardContent = ({
  stepData,
  image,
  number,
  onChangeImage,
  onChangeData,
  classes
}) => {
  const localClasses = useStyles();
  const language = useSelector(getLanguage);
  const { formatMessage } = useIntl();
  const [utensils] = useUtensils();
  const { data } = useContext(FormContext);
  const { ppiDetails = [] } = data;
  const choosenPpiDataIds = getPpiDataIdsFromPpiDetails(ppiDetails);
  const ppiDataList = useSelector(getPpiDataForIds(choosenPpiDataIds)).toJS();
  const sortedUtensils = [];

  const localizedInstructionField = `info.${language}.instruction`;
  const instruction = _.chain(stepData)
    .get(localizedInstructionField)
    .defaultTo('')
    .value();
  const ppiEnrichmentId = _.defaultTo(stepData.ppiEnrichmentId, '');
  const utensilId = _.defaultTo(stepData.utensilId, '');
  const utensilCode = _.defaultTo(stepData.utensilCode, '');

  if (utensils.length > 0) {
    const globalUtensils = utensils.filter(i => i.code.includes('IFS'));
    const localUtensils = utensils.filter(i => !i.code.includes('IFS'));

    if (localUtensils.length > 0) {
      const sortedLocalUtensils = sortBy(
        localUtensils,
        `info.${language}.name`
      );
      sortedUtensils.push(...sortedLocalUtensils);
    }

    if (globalUtensils.length > 0) {
      const sortedGlobalUtensils = sortBy(
        globalUtensils,
        `info.${language}.name`
      );
      sortedUtensils.push(...sortedGlobalUtensils);
    }
  }
  const sortedPpiList = sortBy(
    ppiDataList,
    `info.${language}.presentationName`
  );
  const changeDataHandler = event => {
    if (event.target.value === undefined) {
      return onChangeData('utensilCode', event.target.value);
    }
    const code = event.target.value;
    const usedUtensil = utensils && utensils.find(i => i.code === code);
    return (
      onChangeData('utensilId', usedUtensil.id),
      onChangeData('utensilCode', code)
    );
  };

  return (
    <>
      <ImageWithDropzone
        image={image}
        thumbnailHeight={PlateItRightImageThumbnail.Height}
        thumbnailWidth={PlateItRightImageThumbnail.Width}
        onUploadImage={onChangeImage}
      />
      <CardContent classes={{ root: localClasses.cardContentFieldsRoot }}>
        <FormControl
          margin="dense"
          fullWidth
          variant="filled"
          classes={{ root: classes.formControlRoot }}
        >
          <InputLabel htmlFor="ingredient-label">Ingredient</InputLabel>
          <Select
            id="ingredient-label"
            classes={{ root: classes.inputRoot }}
            value={ppiEnrichmentId}
            onChange={event => {
              onChangeData('ppiEnrichmentId', event.target.value);
            }}
            variant="filled"
            inputProps={{
              tabIndex: number
            }}
          >
            <MenuItem key="select-item/none">
              <em>
                <FormattedMessage id="ppsi.ppiForStepsSelectEmpty" />
              </em>
            </MenuItem>
            {sortedPpiList.map(ppiData => (
              <MenuItem key={`select-item/>${ppiData.id}`} value={ppiData.id}>
                {ppiData.info[language].presentationName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {/* <TextField
          label={formatMessage({ id: 'ppsi.instructionStepLabel' })}
          value={instruction}
          multiline
          rows={5}
          variant="filled"
          InputProps={{
            classes: {
              root: classes.inputRoot
            }
          }}
          inputProps={{
            tabIndex: number
          }}
          fullWidth
          onChange={event => {
            onChangeData(localizedInstructionField, event.target.value);
          }}
          margin="dense"
        /> */}
        <TextEditor
          value={instruction}
          onChange={content => onChangeData(localizedInstructionField, content)}
          charLimit={300} // Set character limit
        />
        <FormControl
          margin="dense"
          fullWidth
          variant="filled"
          classes={{ root: classes.formControlRoot }}
        >
          <InputLabel htmlFor="utensil">
            <FormattedMessage id="ppsi.utensilStepLabel" />
          </InputLabel>
          <Select
            id="utensil"
            classes={{ root: classes.inputRoot }}
            value={utensilCode}
            inputProps={{
              tabIndex: number
            }}
            onChange={event => {
              changeDataHandler(event);
            }}
            variant="filled"
          >
            <MenuItem key="select-item/none">
              <em>
                <FormattedMessage id="ppsi.utensilSelectEmpty" />
              </em>
            </MenuItem>
            {sortedUtensils.length > 0 &&
              sortedUtensils.map(utensil => {
                if (
                  Object.prototype.hasOwnProperty.call(utensil.info, language)
                ) {
                  const uTranslation = utensil.info[language];
                  return (
                    <MenuItem
                      classes={
                        utensil.code.includes('IFS')
                          ? { root: localClasses.menuGlobalSelect }
                          : { root: localClasses.menuLocalSelect }
                      }
                      key={`select-item/${utensil.code}`}
                      value={utensil.code}
                    >
                      {uTranslation.name}
                    </MenuItem>
                  );
                }
              })}
          </Select>
        </FormControl>
      </CardContent>
    </>
  );
};

export default StepCardContent;
